import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { Timeoff } from "../../types";
import { Clock, CheckCircle, XCircle, Info, ArrowLeft } from "lucide-react";
import { clearSelectedTimeoffForView } from "../../redux/features/timeoff/slice";
import { DateTimeUtil } from "../../utils";

const statusColors: Record<string, string> = {
    Approved: "bg-green-100 text-green-600 border-green-300",
    Pending: "bg-yellow-100 text-yellow-600 border-yellow-300",
    Rejected: "bg-red-100 text-red-600 border-red-300",
    Cancelled: "bg-gray-100 text-gray-600 border-gray-300",
};

const getStatusIcon = (status: string) => {
    switch (status) {
        case "Approved":
            return <CheckCircle className="w-5 h-5 text-green-600" />;
        case "Pending":
            return <Clock className="w-5 h-5 text-yellow-600" />;
        case "Rejected":
            return <XCircle className="w-5 h-5 text-red-600" />;
        default:
            return <Info className="w-5 h-5 text-gray-600" />;
    }
};

// Extract start_time from first entry and end_time from last entry in details_json
const getTimeBoundaries = (selectedTimeoff: Timeoff) => {
    const { details_json } = selectedTimeoff;

    if (!details_json || !details_json.days || details_json.days.length === 0) {
        return {
            formattedRange: DateTimeUtil.formatDateTimeRange(selectedTimeoff.start_date, selectedTimeoff.end_date),
        };
    }

    const firstDay = details_json.days[0];
    const lastDay = details_json.days[details_json.days.length - 1];

    const fullStart = `${selectedTimeoff.start_date} ${firstDay.start_time ?? "00:00:00"}`;
    const fullEnd = `${selectedTimeoff.end_date} ${lastDay.end_time ?? "23:59:59"}`;

    return {
        formattedRange: DateTimeUtil.formatDateTimeRangeUtc(fullStart, fullEnd),
    };
};

const TimeOffView: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const selectedTimeoff: Timeoff | undefined = useSelector((state: RootState) => state.timeoff.selectedTimeoffForView);

    // Clear Redux state when user navigates back via browser or button
    useEffect(() => {
        const handleBack = () => {
            dispatch(clearSelectedTimeoffForView());
        };

        window.addEventListener("popstate", handleBack);

        return () => {
            window.removeEventListener("popstate", handleBack);
        };
    }, [dispatch]);

    // Handle back navigation
    const handleBack = () => {
        dispatch(clearSelectedTimeoffForView());
        navigate(-1);
    };

    if (!selectedTimeoff) {
        return (
            <div className="text-center text-gray-600 py-6">
                <p>No time off selected for viewing.</p>
                <button onClick={handleBack} className="mt-4 flex items-center bg-blue-600 text-white px-4 py-2 rounded-md shadow-md hover:bg-blue-700 transition">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                </button>
            </div>
        );
    }

    const { formattedRange } = getTimeBoundaries(selectedTimeoff);

    return (
        <div className="w-full max-w-screen mx-auto bg-white shadow-lg rounded-lg p-8">
            {/* Back Button */}
            <div className="flex items-center mb-6">
                <button onClick={handleBack} className="text-blue-600 hover:text-blue-700 transition flex">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                </button>
            </div>

            {/* Reason at the Top */}
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Time Off Request Details</h2>
                <p className="mt-2 text-gray-600 text-lg italic">Reason: {selectedTimeoff.reason || "No reason provided"}</p>
            </div>

            {/* Summary Section */}
            <div className="">
                <table className="table-auto text-gray-700 border-collapse">
                    <tbody>
                        <tr className="border-b border-gray-300">
                            <td className="py-2 font-medium pr-4">Dates:</td>
                            <td className="py-2 text-gray-900 text-right pl-4">{formattedRange}</td>
                        </tr>

                        <tr className="border-b border-gray-300">
                            <td className="py-2 font-medium pr-4">Category:</td>
                            <td className="py-2 text-gray-900 text-right pl-4">{selectedTimeoff.category_code || "N/A"}</td>
                        </tr>

                        <tr className="border-b border-gray-300">
                            <td className="py-2 font-medium pr-4">Hours Requested:</td>
                            <td className="py-2 text-gray-900 text-right pl-4">{selectedTimeoff.hours_requested || "0"}h</td>
                        </tr>

                        <tr className="border-b border-gray-300">
                            <td className="py-2 font-medium pr-4">Status:</td>
                            <td className="py-2 text-right pl-4">
                                <span className={`inline-flex items-center px-3 py-1  text-sm font-medium ${statusColors[selectedTimeoff.status || "Pending"]}`}>
                                    {getStatusIcon(selectedTimeoff.status || "Pending")}
                                    <span className="ml-2">{selectedTimeoff.status || "Pending"}</span>
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Details Breakdown (Scrollable) */}
            <div className="mt-6 h-64 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Request Breakdown</h3>
                <table className="w-full max-w-5xl text-sm text-gray-600">
                    <thead className="border-b bg-gray-200">
                        <tr>
                            <th className="py-2 px-3 text-left">Date</th>
                            <th className="py-2 px-3 text-left">Start Time</th>
                            <th className="py-2 px-3 text-left">End Time</th>
                            <th className="py-2 px-3 text-left">Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedTimeoff.details_json?.days.map((day, i) => {
                            const fullStartTime = `${day.date} ${day.start_time}`;
                            const fullEndTime = `${day.date} ${day.end_time}`;

                            // Calculate amount dynamically if not provided
                            const calculatedAmount = day.amount ?? DateTimeUtil.getDuration(fullStartTime, fullEndTime);

                            return (
                                <tr key={i} className="border-b">
                                    <td className="py-2 px-3">{DateTimeUtil.formatDate(day.date, "MMM dd, yyyy")}</td>
                                    <td className="py-2 px-3">{DateTimeUtil.getFormattedTime(`${day.date} ${day.start_time}`)}</td>
                                    <td className="py-2 px-3">{DateTimeUtil.getFormattedTime(`${day.date} ${day.end_time}`)}</td>
                                    <td className="py-2 px-3">{calculatedAmount}h</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TimeOffView;
